import { getRum } from '../rum';

/**
 * Reports Content-Security-Policy violations.
 *
 * A meta policy cannot be report-only — that form is header-only, and the header
 * is deploy-time, which is the whole reason the policy lives in a meta tag. So
 * there is no dry run to be had: the first thing a policy that is too tight does
 * is break a screen.
 *
 * This is what stands in for the dry run. Every refusal is recorded, so an
 * over-tight policy shows up as a violation with a directive and a URL rather
 * than as a support case describing a blank panel. It does not prevent the
 * breakage; it makes the breakage legible, and legible in the minutes after a
 * rollout rather than the days after.
 *
 * Deliberately not throttled by anything cleverer than a seen-set: a violation
 * that repeats on every render would otherwise fill the log with one fact.
 */

/** One entry per directive-and-blocked-origin pair; the tenth is as informative as the first. */
const reported = new Set<string>();

/** Kept low: this exists to surface a mistake, not to measure it. */
const MAX_REPORTED = 25;

/**
 * The refusals seen so far, as data rather than as log lines.
 *
 * Kept because the admin screen can act on them: an operator who has to retype a
 * host from a console message will mistype it, and the field they are typing into
 * needs the directive too, which no amount of retyping supplies. The set survives
 * moving between screens — nothing here reloads the page — so a refusal noticed
 * on the Resource Hub is still offered on the Security screen. It does not
 * survive a reload, and the screen says so rather than implying a record.
 */
const refusals: CspRefusal[] = [];

export interface CspRefusal {
	directive: string;
	origin: string;
}

/** What has been refused since this page loaded, in the order it happened. */
export const refusalsSeen = (): CspRefusal[] => [...refusals];

/** Testing seam: the module holds process-wide state by design. */
export const forgetRefusals = (): void => {
	refusals.length = 0;
	reported.clear();
};

/**
 * A blocked address reduced to its origin.
 *
 * The path and query are dropped for safety, not tidiness. A refused request can
 * carry a credential in its query — a presigned URL's signature, a vendor's
 * application key, a signed CDN token — and this value is written to a console
 * line an operator will paste into a ticket and to a CloudWatch RUM record that
 * is kept. Neither is a place for one.
 *
 * Nothing diagnostic is lost. A request is only refused when its host is absent
 * from the directive, so the host is always the whole of the fix; a path never
 * changes the answer. It also makes the de-duplication honest, since two paths
 * on one blocked host were previously two of the twenty-five slots.
 *
 * Keywords the browser sends in place of a URL (`inline`, `eval`) and opaque
 * schemes (`data:`, `blob:`) have no origin to take, so they are named as they
 * come.
 */
export const blockedOrigin = (blockedURI: string): string => {
	if (!blockedURI) {
		return 'inline';
	}
	try {
		const url = new URL(blockedURI);
		return url.origin && url.origin !== 'null' ? url.origin : url.protocol;
	} catch {
		return blockedURI;
	}
};

export const describeViolation = (event: SecurityPolicyViolationEvent): string => {
	// The blocked origin is the useful half — it names the host to add, or the
	// feature that was quietly relying on something the policy forbids.
	const blocked = blockedOrigin(event.blockedURI);
	return `${event.effectiveDirective || event.violatedDirective} blocked ${blocked}`;
};

/**
 * Starts listening. Safe to call when no policy is in force: with nothing to
 * violate, nothing fires.
 */
export const reportPolicyViolations = (target: EventTarget = document): void => {
	target.addEventListener('securitypolicyviolation', (event) => {
		const violation = event as SecurityPolicyViolationEvent;
		const key = describeViolation(violation);
		if (reported.has(key) || reported.size >= MAX_REPORTED) {
			return;
		}
		reported.add(key);
		refusals.push({
			directive: violation.effectiveDirective || violation.violatedDirective,
			origin: blockedOrigin(violation.blockedURI)
		});

		const detail = {
			directive: violation.effectiveDirective || violation.violatedDirective,
			blockedURI: blockedOrigin(violation.blockedURI),
			// Where in our own code hit the limit, when the browser tells us.
			source: violation.sourceFile ? `${violation.sourceFile}:${violation.lineNumber}` : violation.documentURI
		};

		// The console line is for whoever is watching a rollout; RUM is for
		// everyone who was not.
		console.warn('[CSP] ' + key, detail);
		try {
			getRum()?.recordError(new Error(`CSP: ${key}`));
		} catch {
			// RUM is optional and must never be the reason a page fails.
		}
	});
};
